import type { ForwardConfirmation, ForwardProvider } from "../schemas/index.js";
import type { ParsedMessage } from "../parse/index.js";

export const FORWARD_PROVIDER_LABELS: Record<ForwardProvider, string> = { gmail: "Gmail", yahoo: "Yahoo", cloudflare: "Cloudflare" };

/**
 * Gmail sends one mail from `forwarding-noreply@google.com` before it forwards
 * anything, with the subject "(#<code>) Gmail Forwarding Confirmation - ...".
 * Yahoo's confirmation comes from a yahoo-inc.com address and contains a
 * verify link. Cloudflare Email Routing sends "Verify your Email Routing
 * address" with a verify link and no code. Returns the code and link, or
 * undefined for any other mail. The Yahoo match is by sender domain and
 * wording; it is not confirmed against a real Yahoo mail.
 */
export function readForwardConfirmation(message: ParsedMessage, now: Date): ForwardConfirmation | undefined {
  const at = now.toISOString();
  const sender = message.from.email;

  if (sender === "forwarding-noreply@google.com" && /forwarding confirmation/i.test(message.subject)) {
    const code = /\(#(\d{5,12})\)/.exec(message.subject)?.[1] ?? /confirmation code:\s*(\d{5,12})/i.exec(message.text)?.[1];
    const link = /https:\/\/mail-settings\.google\.com\/\S+/.exec(message.text)?.[0];
    return withOptional({ provider: "gmail", at }, code, link);
  }

  if (/@([a-z0-9-]+\.)*yahoo-inc\.com$/.test(sender) && /forward/i.test(message.subject + message.text)) {
    const code = /code[:\s]+([A-Z0-9]{4,12})\b/i.exec(message.text)?.[1];
    const link = /https:\/\/[a-z0-9.-]*yahoo\.com\/\S+/i.exec(message.text)?.[0];
    return withOptional({ provider: "yahoo", at }, code, link);
  }

  if (sender === "noreply@notify.cloudflare.com" && /verify your email routing address/i.test(message.subject)) {
    const link = /https:\/\/dash\.cloudflare\.com\/email_fwdr\/verify\?\S+/.exec(message.text)?.[0];
    return withOptional({ provider: "cloudflare", at }, undefined, link);
  }

  return undefined;
}

function withOptional(base: ForwardConfirmation, code: string | undefined, link: string | undefined): ForwardConfirmation {
  return { ...base, ...(code === undefined ? {} : { code }), ...(link === undefined ? {} : { link }) };
}
